import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { CalendarMonth, LocationOn, People, Event as EventIcon } from '@mui/icons-material';
import eventsService from '../utils/eventsService';
import Loader from '../components/Loader';
import { snackbar } from '../utils/showMessage';

const Events = () => {
    const [upcomingEvents, setUpcomingEvents] = useState([]);
    const [completedEvents, setCompletedEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    // مقدار اولیه تب فعال را از query string بخوان
    const location = useLocation();
    const navigate = useNavigate();
    const getInitialTab = () => {
        const params = new URLSearchParams(location.search);
        const tab = params.get('tab');
        return tab === 'completed' ? 'completed' : 'upcoming';
    };
    const [activeTab, setActiveTab] = useState(getInitialTab()); // upcoming | completed

    // هر بار که query string تغییر کند، تب را هماهنگ کن
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const tab = params.get('tab');
        if ((tab === 'completed' || tab === 'upcoming') && tab !== activeTab) {
            setActiveTab(tab);
        }
    }, [location.search]);

    // هر بار که تب تغییر کند، query string را هماهنگ کن
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        if (activeTab !== params.get('tab')) {
            params.set('tab', activeTab);
            navigate({ search: params.toString() }, { replace: true });
        }
    }, [activeTab]);
    const fileBaseUrl = process.env.REACT_APP_FILE_BASE_URL || process.env.REACT_APP_API_BASE_URL || '';

    useEffect(() => {
        loadEvents();
    }, []);

    const loadEvents = async () => {
        try {
            setLoading(true);
            const [upcomingRes, completedRes] = await Promise.all([
                eventsService.getUpcoming(20),
                eventsService.getCompleted(1, 20),
            ]);

            setUpcomingEvents(upcomingRes.data || []);
            setCompletedEvents(completedRes.data?.events || []);
        } catch (error) {
            console.error('Error loading events:', error);
            snackbar('خطا در بارگذاری مراسمات');
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return new Intl.DateTimeFormat('fa-IR', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        }).format(date);
    };

    const EventCard = ({ event }) => (
        <Link
            to={`/eventdetail/${event._id}?tab=${activeTab}`}
            className="text-decoration-none"
        >
            <div className="card mb-3 shadow-sm">
                {event.posterImage && (
                    <div className="card-img-top" style={{ height: '200px', overflow: 'hidden' }}>
                        <img
                            src={`${fileBaseUrl}/uploads/documents/${event.posterImage}`}
                            alt={event.title}
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                    </div>
                )}
                <div className="card-body">
                    <h5 className="card-title text-dark">{event.title}</h5>
                    <p className="card-text text-muted small">
                        {event.summary || event.description?.substring(0, 100) + '...'}
                    </p>

                    <div className="d-flex flex-column gap-2 mt-3">
                        <div className="d-flex align-items-center text-muted small">
                            <CalendarMonth sx={{ fontSize: 18, mr: 1 }} />
                            {formatDate(event.eventDate)}
                        </div>

                        {event.location && (
                            <div className="d-flex align-items-center text-muted small">
                                <LocationOn sx={{ fontSize: 18, mr: 1 }} />
                                {event.location}
                            </div>
                        )}

                        {event.capacity && (
                            <div className="d-flex align-items-center text-muted small">
                                <People sx={{ fontSize: 18, mr: 1 }} />
                                ظرفیت: {event.capacity} نفر
                            </div>
                        )}
                    </div>

                    {event.status === 'completed' && event.gallery?.length > 0 && (
                        <div className="mt-3">
                            <span className="badge bg-success">
                                <EventIcon sx={{ fontSize: 14, verticalAlign: 'middle', ml: 0.5 }} />
                                {event.gallery.length} تصویر از مراسم
                            </span>
                        </div>
                    )}
                </div>
            </div>
        </Link>
    );

    if (loading) return <Loader />;

    return (
        <div className="main-container container">
            {/* <div className="row mb-3">
                <div className="col-12">
                    <h4 className="mb-3">مراسمات</h4>
                </div>
            </div> */}

            {/* Tabs */}
            <div className="row mb-3">
                <div className="col-12">
                    <div className="d-flex gap-2">
                        <button
                            type="button"
                            className={`btn flex-fill ${activeTab === 'upcoming' ? 'btn-primary' : 'btn-outline-primary'}`}
                            onClick={() => setActiveTab('upcoming')}
                        >
                            مراسمات آینده ({upcomingEvents.length})
                        </button>
                        <button
                            type="button"
                            className={`btn flex-fill ${activeTab === 'completed' ? 'btn-primary' : 'btn-outline-primary'}`}
                            onClick={() => setActiveTab('completed')}
                        >
                            مراسمات برگزار شده ({completedEvents.length})
                        </button>
                    </div>
                </div>
            </div>

            {/* Content */}
            <div className="row">
                <div className="col-12">
                    {activeTab === 'upcoming' ? (
                        upcomingEvents.length > 0 ? (
                            upcomingEvents.map((event) => (
                                <EventCard key={event._id} event={event} />
                            ))
                        ) : (
                            <div className="alert alert-info text-center">
                                <EventIcon sx={{ fontSize: 48, mb: 2 }} />
                                <p>مراسم آینده‌ای ثبت نشده است</p>
                            </div>
                        )
                    ) : (
                        completedEvents.length > 0 ? (
                            completedEvents.map((event) => (
                                <EventCard key={event._id} event={event} />
                            ))
                        ) : (
                            <div className="alert alert-info text-center">
                                <EventIcon sx={{ fontSize: 48, mb: 2 }} />
                                <p>مراسمی برگزار نشده است</p>
                            </div>
                        )
                    )}
                </div>
            </div>
        </div>
    );
};

export default Events;
